import { ChangeEvent, FormEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import MultipleLineFieldV2 from "@components/FormsV2/MultipleLineFieldV2/MultipleLineFieldV2";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { CAUSE_OPTIONS } from "../constants/causes";
import type { ChangeOrderCause } from "../constants/causes";
import { isHttpUrl } from "../constants/url";
import type { ChangeOrderFormValues } from "../types";
import LineSelect from "./LineSelect";

export interface ChangeOrderFormProps {
  id: string;
  mode: "new" | "edit";
  projectId: string;
  initialValues: ChangeOrderFormValues;
  readOnly?: boolean;
  childProjectId?: string | null;
  onSubmit: (values: ChangeOrderFormValues) => void;
}

interface Errors {
  cause?: string;
  externalUrl?: string;
}

export const validate = (values: ChangeOrderFormValues): Errors => {
  const errors: Errors = {};
  if (!values.cause) errors.cause = "Select a cause";
  const url = values.externalUrl.trim();
  if (url && !isHttpUrl(url)) errors.externalUrl = "Enter an http(s) URL";
  return errors;
};

const TYPE_OPTIONS = [
  { referenceOnly: false, label: "Add scope in ScopeStack" },
  { referenceOnly: true, label: "Reference only (tracked in another system)" },
];

export const ChangeOrderForm = ({
  id,
  mode,
  projectId,
  initialValues,
  readOnly = false,
  childProjectId,
  onSubmit,
}: ChangeOrderFormProps): JSX.Element => {
  const [values, setValues] = useState<ChangeOrderFormValues>(initialValues);
  const [errors, setErrors] = useState<Errors>({});

  const set = <K extends keyof ChangeOrderFormValues>(
    key: K,
    v: ChangeOrderFormValues[K]
  ) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    if (key in errors) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (readOnly) return;
    const found = validate(values);
    setErrors(found);
    if (found.cause || found.externalUrl) return;
    onSubmit({
      ...values,
      externalUrl: values.externalUrl.trim(),
      sourceItems: values.referenceOnly ? [] : values.sourceItems,
    });
  };

  const selectedCause = CAUSE_OPTIONS.find((o) => o.value === values.cause);

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit} noValidate>
      {mode === "new" ? (
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          {TYPE_OPTIONS.map((opt) => (
            <Form.Check
              key={String(opt.referenceOnly)}
              type="radio"
              id={`${id}-type-${opt.referenceOnly ? "reference" : "authored"}`}
              name={`${id}-type`}
              label={opt.label}
              checked={values.referenceOnly === opt.referenceOnly}
              disabled={readOnly}
              onChange={() =>
                setValues((prev) => ({
                  ...prev,
                  referenceOnly: opt.referenceOnly,
                  sourceItems: [],
                }))
              }
            />
          ))}
        </Form.Group>
      ) : (
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <div>
            {values.referenceOnly ? (
              "Reference only"
            ) : (
              <>
                Authored
                {childProjectId && (
                  <>
                    {" — "}
                    <a href={`/projects/${childProjectId}/project_editor`}>
                      open the change-order project
                    </a>
                  </>
                )}
              </>
            )}
          </div>
        </Form.Group>
      )}

      <div className="row">
        <div className="col-sm-6">
          <RequiredReactSelect
            label="Cause"
            value={selectedCause}
            onChange={(opt: any) =>
              set("cause", (opt?.value ?? "") as ChangeOrderCause | "")
            }
            options={CAUSE_OPTIONS}
            placeholder="Select…"
            isDisabled={readOnly}
            required
          />
          {errors.cause && (
            <Form.Text className="text-danger">{errors.cause}</Form.Text>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <Form.Label htmlFor={`${id}-cause-note`}>Note</Form.Label>
          <MultipleLineFieldV2
            label={null}
            id={`${id}-cause-note`}
            rows={3}
            value={values.causeNote}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("causeNote", e.target.value)
            }
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor={`${id}-external-reference`}>
            External Reference
          </Form.Label>
          <TextField
            label={null}
            id={`${id}-external-reference`}
            value={values.externalReference}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("externalReference", e.target.value)
            }
            disabled={readOnly}
          />
        </div>
        <div className="col-sm-6">
          <Form.Label htmlFor={`${id}-external-url`}>External URL</Form.Label>
          <TextField
            label={null}
            id={`${id}-external-url`}
            value={values.externalUrl}
            placeholder="https://"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("externalUrl", e.target.value)
            }
            disabled={readOnly}
          />
          {errors.externalUrl && (
            <Form.Text className="text-danger">{errors.externalUrl}</Form.Text>
          )}
        </div>
      </div>

      {mode === "new" && !values.referenceOnly && (
        <LineSelect
          projectId={projectId}
          onChange={(items) => set("sourceItems", items)}
        />
      )}
    </Form>
  );
};

export default ChangeOrderForm;
